import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { pushEvent } from "shared-ui/src/instrumentation/gtm";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { STOREFRONT_SWITCHER_FEATURE_FLAG } from "../constants";
import {
  StorefrontSwitcherDialog,
  type StorefrontSwitcherSource,
} from "./StorefrontSwitcherDialog";
import { useGetUserContext } from "./useUserContext";
import { StorefrontSwitchResultNotice } from "./StorefrontSwitchResultNotice";

interface StorefrontSwitcherContextValue {
  /** The switcher replaces the legacy change-catalog links and tenant dialog. */
  enabled: boolean;
  canOpenTenant: boolean;
  canOpenStorefront: boolean;
  open: (source: StorefrontSwitcherSource) => void;
}

const disabled: StorefrontSwitcherContextValue = {
  enabled: false,
  canOpenTenant: false,
  canOpenStorefront: false,
  open: () => undefined,
};

const StorefrontSwitcherContext =
  createContext<StorefrontSwitcherContextValue>(disabled);

export function StorefrontSwitcherProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { enabled: flagEnabled = false } = useFeatureFlag(
    STOREFRONT_SWITCHER_FEATURE_FLAG,
    false,
  );
  const { data } = useGetUserContext(flagEnabled);
  const [openSource, setOpenSource] = useState<StorefrontSwitcherSource | null>(
    null,
  );

  const open = useCallback((source: StorefrontSwitcherSource) => {
    pushEvent({
      event: "storefront_switch_open",
      name: "storefront_switch_open",
      section: source,
      action: "open",
    });
    setOpenSource(source);
  }, []);

  const value = useMemo<StorefrontSwitcherContextValue>(() => {
    if (!flagEnabled) {
      return disabled;
    }
    const tenants = data?.tenants ?? [];
    const currentTenant = tenants.find((tenant) => tenant.isCurrent);
    // In tenant-only mode there is no current storefront, so a single storefront is still a choice.
    const minimumStorefronts = data?.currentOrgUnitId == null ? 1 : 2;
    return {
      enabled: true,
      canOpenTenant: (data?.canSwitchTenant ?? false) && tenants.length > 1,
      // An unfinished switch can only be retried from the dialog, so keep an entry point open for it.
      canOpenStorefront:
        (data?.canSwitchStorefront ?? false) &&
        ((currentTenant?.storefronts?.length ?? 0) >= minimumStorefronts ||
          !!data?.pendingSwitch),
      open,
    };
  }, [flagEnabled, data, open]);

  return (
    <StorefrontSwitcherContext.Provider value={value}>
      {children}
      {flagEnabled && <StorefrontSwitchResultNotice />}
      {flagEnabled && openSource && (
        <StorefrontSwitcherDialog
          open
          source={openSource}
          expandCurrentTenant={openSource !== "tenant_nametag"}
          onClose={() => setOpenSource(null)}
        />
      )}
    </StorefrontSwitcherContext.Provider>
  );
}

export function useStorefrontSwitcher() {
  return useContext(StorefrontSwitcherContext);
}
